/**
 * Setup Storage Utilities
 * Manages localStorage persistence for the onboarding/setup flow
 */

/**
 * Whitelist of UIDs that can access the setup feature
 */
export const SETUP_ALLOWED_UIDS = ['STORE MANAGER', 'asaf-dm11', 'asaf-dm9' ,'tomi-h', 'prod-nico', 'prod-ricky', 'prod-serena']

/**
 * Checks if user is allowed to access setup feature
 * @param uid - User ID
 * @returns true if user is in whitelist
 */
export const isSetupAllowedForUser = (uid: string | null | undefined): boolean => {
  if (!uid) return false
  return SETUP_ALLOWED_UIDS.includes(uid)
}

export type SetupStepStatus = 'completed' | 'skipped' | 'pending'

export interface SetupStep {
  id: number
  title: string
  description: string
  route: string
  status: SetupStepStatus
  action?: () => void
}

export interface SetupState {
  currentStep: number
  steps: {
    [key: number]: {
      status: SetupStepStatus
    }
  }
  isFinalized: boolean
}

export const STEPS = {
  FEED: 1,
  ORGANIZATION: 2,
  AI_FEED: 3,
  REPORTS: 4,
  RANKINGS_LIST: 5,
  FIND_SALES_OPPORTUNITIES: 6,
}

// Storage keys
const SETUP_STORAGE_KEY = 'storee_setup'
export const SETUP_IN_PROGRESS_KEY = 'setup_in_progress'

/**
 * Gets the setup in-progress key for a specific user
 * @param uid - User ID
 * @returns User-specific setup in-progress key
 */
export const getSetupInProgressKey = (uid: string): string => {
  return `${SETUP_IN_PROGRESS_KEY}_${uid}`
}

// Setup configuration
export const TOTAL_SETUP_STEPS = Object.keys(STEPS).length

/**
 * Retrieves setup state for a specific user from localStorage
 * @param uid - User ID
 * @returns Setup state or null if not found
 */
export const getSetupState = (uid: string): SetupState | null => {
  try {
    const data = localStorage.getItem(SETUP_STORAGE_KEY)
    if (!data) return null

    const parsed = JSON.parse(data)
    const userState = parsed[uid]

    if (!userState) return null

    // Migration: Add isFinalized field if it doesn't exist
    if (userState.isFinalized === undefined) {
      userState.isFinalized = false
      saveSetupState(uid, userState)
    }

    return userState
  } catch (error) {
    console.error('Error reading setup state:', error)
    return null
  }
}

/**
 * Saves setup state for a specific user to localStorage
 * @param uid - User ID
 * @param state - Setup state to save
 */
export const saveSetupState = (uid: string, state: SetupState): void => {
  try {
    const data = localStorage.getItem(SETUP_STORAGE_KEY)
    const parsed = data ? JSON.parse(data) : {}

    parsed[uid] = state
    localStorage.setItem(SETUP_STORAGE_KEY, JSON.stringify(parsed))
  } catch (error) {
    console.error('Error saving setup state:', error)
  }
}

/**
 * Initializes setup state for a new user with all steps pending
 * @param uid - User ID
 * @returns Initialized setup state
 */
export const initializeSetupState = (uid: string): SetupState => {
  const initialState: SetupState = {
    currentStep: STEPS.FEED,
    steps: {
      [STEPS.FEED]: { status: 'pending' },
      [STEPS.ORGANIZATION]: { status: 'pending' },
      [STEPS.AI_FEED]: { status: 'pending' },
      [STEPS.REPORTS]: { status: 'pending' },
      [STEPS.RANKINGS_LIST]: { status: 'pending' },
      [STEPS.FIND_SALES_OPPORTUNITIES]: { status: 'pending' },
    } as { [key: number]: { status: SetupStepStatus } },
    isFinalized: false,
  }

  saveSetupState(uid, initialState)
  return initialState
}

/**
 * Updates the status of a specific step
 * Auto-advances to next step when completed or skipped
 * @param uid - User ID
 * @param stepId - Step ID (1-5)
 * @param status - New status for the step
 */
export const updateStepStatus = (uid: string, stepId: number, status: SetupStepStatus): void => {
  const state = getSetupState(uid)
  if (!state) return

  state.steps[stepId] = { status }

  // Auto-advance to next step if current step is completed/skipped
  if (status === 'completed' || status === 'skipped') {
    if (stepId < TOTAL_SETUP_STEPS) {
      state.currentStep = stepId + 1
    }
  }

  saveSetupState(uid, state)
}

/**
 * Checks if all setup steps are completed
 * @param uid - User ID
 * @returns true if all steps are completed, false otherwise
 */
export const areAllStepsCompleted = (uid: string): boolean => {
  const state = getSetupState(uid)
  if (!state) return false

  return Object.values(state.steps).every((step) => step.status === 'completed')
}

/**
 * Checks if setup is fully completed and finalized
 * @param uid - User ID
 * @returns true if setup is complete and finalized, false otherwise
 */
export const isSetupCompleted = (uid: string): boolean => {
  const state = getSetupState(uid)
  if (!state) return false

  return state.isFinalized === true
}

/**
 * Finalizes the setup after all steps are completed
 * @param uid - User ID
 */
export const finalizeSetup = (uid: string): void => {
  const state = getSetupState(uid)
  if (!state) return

  state.isFinalized = true
  saveSetupState(uid, state)
  localStorage.removeItem(getSetupInProgressKey(uid))
}

export const isCurrentStepCompleted = (uid: string): boolean => {
  const state = getSetupState(uid)
  const currentStep = getCurrentStep(uid)
  if (!state) return false

  return state.steps[currentStep]?.status === 'completed'
}

/**
 * Checks if user has not started setup yet
 * Returns true if user has no setup data OR all steps are pending
 * @param uid - User ID
 * @returns true if setup has not been started
 */
export const isSetupNotStarted = (uid: string): boolean => {
  if (!uid) return false

  const state = getSetupState(uid)

  // No setup data at all
  if (!state) return true

  // Check if all steps are still pending
  const allStepsPending = Object.values(state.steps).every((step) => step.status === 'pending')

  return allStepsPending
}

/**
 * Determines if setup should be shown to the user
 * @param uid - User ID
 * @returns true if setup should be displayed
 */
export const shouldShowSetup = (uid: string): boolean => {
  if (!uid) return false

  const state = getSetupState(uid)

  // New users need setup initialization
  if (!state) return true

  // Show setup if there are incomplete steps
  return !isSetupCompleted(uid)
}

/**
 * Gets the current active step for the user
 * @param uid - User ID
 * @returns Current step number (1-5)
 */
export const getCurrentStep = (uid: string): number => {
  const state = getSetupState(uid)
  return state?.currentStep || 1
}

/**
 * Counts the number of completed steps
 * @param uid - User ID
 * @returns Number of completed steps
 */
export const getCompletedStepsCount = (uid: string): number => {
  const state = getSetupState(uid)
  if (!state) return 0

  return Object.values(state.steps).filter((step) => step.status === 'completed').length
}

/**
 * Resets setup state for a user (for testing purposes)
 * @param uid - User ID
 */
export const resetSetupState = (uid: string): void => {
  try {
    const data = localStorage.getItem(SETUP_STORAGE_KEY)
    if (!data) return

    const parsed = JSON.parse(data)
    delete parsed[uid]
    localStorage.setItem(SETUP_STORAGE_KEY, JSON.stringify(parsed))
    localStorage.removeItem(getSetupInProgressKey(uid))
  } catch (error) {
    console.error('Error resetting setup state:', error)
  }
}

/**
 * Completely clears all setup data from localStorage
 * Removes storee_setup and all setup_in_progress flags
 * Use with caution - this will reset setup for ALL users
 */
export const clearAllSetupData = (): void => {
  try {
    localStorage.removeItem(SETUP_STORAGE_KEY)

    const allKeys = Object.keys(localStorage)
    allKeys.forEach((key) => {
      if (key.startsWith(SETUP_IN_PROGRESS_KEY)) {
        localStorage.removeItem(key)
      }
    })

    console.log('✅ All setup data cleared from localStorage')
  } catch (error) {
    console.error('Error clearing setup data:', error)
  }
}

// Expose to window for easy access in browser console (development only)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  ;(window as any).clearSetupData = clearAllSetupData
  ;(window as any).resetSetupState = resetSetupState
  console.log('🔧 Setup debug functions available:')
  console.log('  - window.clearSetupData() - Clear all setup data')
  console.log('  - window.resetSetupState(uid) - Reset setup for specific user')
}
